import * as React from 'react';
import { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import MultiSelectOTA from '~/features/base/components/forms/ota/MultiselectOTA';
import SelectOTA from '~/features/base/components/forms/ota/SelectOTA';
import { useFetchBaseSwVersionsQuery } from '~/api/deliverables/DeliverablesManagement.api';
import { UNKNOWN } from '~/features/base/constants/filterOptions';
import { Checkbox } from "@rio-cloud/rio-uikit";

interface BaseSwFormItemProps {
    swVersion?: string[];
    swVersionRange?: { minVersion?: string; maxVersion?: string };
    onSwVersionChange: (versions: string[]) => void;
    onSwVersionRangeChange: (range: { minVersion?: string; maxVersion?: string }) => void;
}

const BaseSwFormItem = ({ swVersion, swVersionRange, onSwVersionChange, onSwVersionRangeChange }: BaseSwFormItemProps) => {
    const intl = useIntl();
    const { data: baseSwVersions = [] } = useFetchBaseSwVersionsQuery({});

    const [useRange, setUseRange] = useState(() => !!(swVersionRange && (swVersionRange.minVersion || swVersionRange.maxVersion)));

    React.useEffect(() => {
        const hasRange = !!(swVersionRange && (swVersionRange.minVersion || swVersionRange.maxVersion));
        if (hasRange && !useRange) {
            setUseRange(true);
        }
    }, [swVersionRange, useRange]);

    const options = [
        { label: UNKNOWN, value: UNKNOWN, id: UNKNOWN },
        ...baseSwVersions.map(version => ({
            label: version.shortBaseSwVersion,
            value: version.shortBaseSwVersion,
            id: version.shortBaseSwVersion
        }))
    ];

    const handleModeChange = (checked: boolean) => {
        setUseRange(checked);
        if (checked) {
            onSwVersionRangeChange({});
        } else {
            onSwVersionChange([]);
        }
    };

    const handleVersionsChange = (versions) => {
        onSwVersionChange(versions || []);
    };

    const handleRangeChange = (field: 'minVersion' | 'maxVersion', selectedOption) => {
        const version = Array.isArray(selectedOption) ? selectedOption[0]?.value : selectedOption?.value;
        const currentRange = swVersionRange || {};
        onSwVersionRangeChange({ ...currentRange, [field]: version });
    };

    return (
        <div className='form-group'>
            <label className='control-label'>
                <FormattedMessage id='intl-msg:baseSoftwareVersion'/>
            </label>

            <div className='margin-bottom-10'>
                <Checkbox
                    checked={useRange}
                    label={ <FormattedMessage id='intl-msg:useVersionRange' defaultMessage='Use version range'/>}
                    onChange={(e) => handleModeChange(e.target.checked)}
                >

                </Checkbox>
            </div>

            {useRange ? (
                <div className='display-flex gap-10'>
                    <div className='width-50pct'>
                        <label className='control-label'>
                            <FormattedMessage id='intl-msg:minVersion' defaultMessage='Min Version'/>
                        </label>
                        <SelectOTA
                            useClear
                            useFilter
                            placeholder={intl.formatMessage({ id: 'intl-msg:minVersion' })}
                            options={options}
                            value={swVersionRange?.minVersion ? [swVersionRange.minVersion] : undefined}
                            onChange={(opt) => handleRangeChange('minVersion', opt)}
                        />
                    </div>
                    <div className='width-50pct'>
                        <label className='control-label'>
                            <FormattedMessage id='intl-msg:maxVersion' defaultMessage='Max Version'/>
                        </label>
                        <SelectOTA
                            useClear
                            useFilter
                            placeholder={intl.formatMessage({ id: 'intl-msg:maxVersion' })}
                            options={options}
                            value={swVersionRange?.maxVersion ? [swVersionRange.maxVersion] : undefined}
                            onChange={(opt) => handleRangeChange('maxVersion', opt)}
                        />
                    </div>
                </div>
            ) : (
                <MultiSelectOTA
                    labelId='intl-msg:specificVersions'
                    options={options.map(o => o.value)}
                    value={swVersion || []}
                    onChange={handleVersionsChange}
                />
            )}
        </div>
    );
};

export default BaseSwFormItem;
